import { APIcallerv2 } from './APIcaller';

/**
 * Paginated external clients of the caller's client, restricted to the given store perimeter.
 * Pagination / sort / search travel in the query string; the store filter travels in the body,
 * as expected by POST /v2/b2b/external-clients (COR-1627). An optional `excludedProductId`
 * drops the clients already having that product in their catalog (COR-1677).
 */
const getPaginatedExternalClients = (
  { page, limit, search, orderBy, orderType },
  { storeIds, excludedProductId = null },
) => {
  const url = `/b2b/external-clients?page=${page}&limit=${limit}&search=${encodeURIComponent(
    search,
  )}&orderBy=${orderBy}&orderType=${orderType}`;

  return APIcallerv2.apiPost(url, { storeIds, excludedProductId });
};

/**
 * All the external clients having the product in their catalog, with the catalog price and the
 * margin computed on the client's default channel (POST /v2/b2b/products/:id/external-clients,
 * COR-1676). Not paginated — resolves to `{ externalClients, defaultChannel }`.
 */
const getProductExternalClients = (productId, { storeIds }) =>
  APIcallerv2.apiPost(`/b2b/products/${productId}/external-clients`, { storeIds });

/**
 * Add products to external client catalogs (POST /v2/b2b/external-clients/products, COR-1677).
 * Existing active lines are skipped server-side; new lines take the product's current price.
 */
const addProductsToCatalogs = (externalClientIds, productIds) =>
  APIcallerv2.apiPost('/b2b/external-clients/products', { externalClientIds, productIds });

/**
 * Remove products from external client catalogs — soft delete server-side, missing lines are
 * ignored (DELETE /v2/b2b/external-clients/products, COR-1677).
 */
const removeProductsFromCatalogs = (externalClientIds, productIds) =>
  APIcallerv2.apiDelete('/b2b/external-clients/products', { externalClientIds, productIds });

/**
 * Update the catalog price of products for the given external clients, historizing the previous
 * price (PATCH /v2/b2b/external-clients/products/price, COR-1668).
 */
const updateCatalogProductsPrice = (externalClientIds, productIds, priceWithTaxes) =>
  APIcallerv2.apiPatch('/b2b/external-clients/products/price', {
    externalClientIds,
    productIds,
    priceWithTaxes,
  });

/**
 * Paginated product catalog of an external client via POST /v2/b2b/external-clients/:id/catalog
 * (COR-1666). Pagination / sort / search travel in the query string; the category, sub-category and
 * central-product filters travel in the body as drizzle condition descriptors
 * (e.g. `{ inArray: [...] }`, `{ eq: true }`). Resolves to `{ catalog, totalCount }`.
 */
const getExternalClientCatalog = (
  externalClientId,
  { page, limit, search, orderBy, orderType },
  filters,
) => {
  const url = `/b2b/external-clients/${externalClientId}/catalog?page=${page}&limit=${limit}&search=${encodeURIComponent(
    search,
  )}&orderBy=${orderBy}&orderType=${orderType}`;

  return APIcallerv2.apiPost(url, filters);
};

/**
 * Whole active catalog of an external client, unpaginated: `limit=0` switches the catalog route to
 * its full retrieval mode (COR-1689), which the order form needs since it displays every catalog
 * line at once. `isKitchen` narrows it to the product type the selected store can prepare.
 * Resolves to `{ catalog, totalCount }`, as the paginated mode does.
 */
const getFullExternalClientCatalog = (externalClientId, { isKitchen }) => {
  const url = `/b2b/external-clients/${externalClientId}/catalog?page=1&limit=0&search=&orderBy=name&orderType=asc`;

  return APIcallerv2.apiPost(url, { isKitchen: { eq: isKitchen } });
};

/**
 * Replace an external client catalog with a clone of another client's one via POST
 * /v2/b2b/external-clients/:id/catalog/clone (COR-1670). Products incompatible with the target
 * store perimeter (central / non-central) are skipped server-side.
 */
const cloneCatalog = (externalClientId, sourceExternalClientId) =>
  APIcallerv2.apiPost(`/b2b/external-clients/${externalClientId}/catalog/clone`, {
    sourceExternalClientId,
  });

/**
 * Active products selectable for an external client catalog, filtered by the kitchen profile of the
 * given stores (POST /v2/b2b/products). Each product carries its `category` / `subCategory` names
 * and an `isKitchen` flag. Not paginated — the full selectable list is returned at once.
 * Passing `externalClientId` excludes the products already present in that client's catalog.
 */
const getSelectableProducts = ({ storeIds, externalClientId = null }) =>
  APIcallerv2.apiPost('/b2b/products', { storeIds, externalClientId });

/**
 * Atomically create an external client with its stores and initial catalog (empty / manual / clone)
 * via POST /v2/b2b/external-clients/create (COR-1626). Resolves to the created client `{ id }`.
 */
const createExternalClient = (payload) =>
  APIcallerv2.apiPost('/b2b/external-clients/create', payload);

/**
 * Fetch an external client detail (info + associated stores) via GET /v2/b2b/external-clients/:id
 * (COR-1633). The catalog is out of scope of this endpoint.
 */
const getExternalClient = (externalClientId) =>
  APIcallerv2.apiGet(`/b2b/external-clients/${externalClientId}`);

/**
 * Year-over-year metrics of an external client via GET /v2/b2b/external-clients/:id/metrics
 * (COR-1723): orders count and revenue incl. taxes of the current year (year-to-date, on delivery
 * dates, drafts excluded). Resolves to `{ ordersCount, revenueWithTaxes }`, each carrying
 * `{ current, previous, evolution }` — `evolution` is a percentage, null when the previous period
 * has no order.
 */
const getExternalClientMetrics = (externalClientId) =>
  APIcallerv2.apiGet(`/b2b/external-clients/${externalClientId}/metrics`);

/**
 * Update an external client information via PATCH /v2/b2b/external-clients/:id (COR-1633).
 * Desassociating every non-central / central store purges the matching catalog products server-side.
 */
const updateExternalClient = (externalClientId, payload) =>
  APIcallerv2.apiPatch(`/b2b/external-clients/${externalClientId}`, payload);

/**
 * Soft-delete an external client along with its store mappings and its catalog via
 * DELETE /v2/b2b/external-clients/:id (COR-1731).
 */
const deleteExternalClient = (externalClientId) =>
  APIcallerv2.apiDelete(`/b2b/external-clients/${externalClientId}`);

const getUpcomingOrders = (externalClientId) =>
  APIcallerv2.apiGet(`/b2b/external-clients/${externalClientId}/upcoming-orders`);

export const externalClient = {
  getPaginatedExternalClients,
  getProductExternalClients,
  getSelectableProducts,
  createExternalClient,
  getExternalClient,
  getExternalClientMetrics,
  updateExternalClient,
  deleteExternalClient,
  getExternalClientCatalog,
  getFullExternalClientCatalog,
  addProductsToCatalogs,
  removeProductsFromCatalogs,
  updateCatalogProductsPrice,
  cloneCatalog,
  getUpcomingOrders,
};
